/**
 * What editing does to a layout, as plain functions: each takes a layout and returns a new one (the
 * placements it didn't touch are the same objects). The viewer turns pointer and key input into these;
 * the tests drive them directly.
 */

import { catalogueItem, type CatalogueItem } from '@mawkeb/shared/catalogue';
import { footprint, placedItem, placedSize, ROW_DEFAULTS, rowsGeometry, toHall, type Aim, type Layout, type Placement, type PropPlacement } from '@mawkeb/shared/layout';
import type { MeasuredModel, Wall } from '@mawkeb/shared/model';
import { closestOnSegment } from '@mawkeb/shared/geometry2d';

export const STEP = Math.PI / 12;

export interface Surfaces {
  /** Height of what one would stand on at (x, y): the stage, a platform, a carpet strip, else the floor. */
  at(x: number, y: number, ignore?: ReadonlySet<string>): number;
}

const newId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `p${Math.random().toString(36).slice(2)}`);

const aim = (x: number, y: number, z: number, yaw: number): Aim => ({ x, y, z, yaw, tilt: 0, roll: 0 });

/** Whether a placement hangs on a wall as it is now placed (a banner may have been moved to a stand). */
export function onWall(p: Placement): boolean {
  const item = catalogueItem(p.item);
  if (!item) return false;
  return (p.kind === 'prop' ? placedItem(p, item) : item).mount === 'wall';
}

/** Turns an angle into -pi..pi. */
export function wrap(a: number): number {
  const t = (((a + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
  return Object.is(t, -0) ? 0 : t;
}

/** The nearest wall to (x, y): where on it, and the turn that faces into the room. */
export function nearestWall(model: MeasuredModel, x: number, y: number): { wall: Wall; x: number; y: number; yaw: number; distance: number } | null {
  let best: { wall: Wall; x: number; y: number; yaw: number; distance: number } | null = null;
  for (const wall of model.walls) {
    const c = closestOnSegment([x, y], wall.a, wall.b);
    if (best && c.distance >= best.distance) continue;
    const dx = wall.b[0] - wall.a[0];
    const dy = wall.b[1] - wall.a[1];
    const len = Math.hypot(dx, dy) || 1;
    // the room is on the left going a to b
    const nx = -dy / len;
    const ny = dx / len;
    best = { wall, x: c.point[0], y: c.point[1], yaw: Math.atan2(-nx, ny), distance: c.distance };
  }
  return best;
}

/** A new placement of a library item at (x, y), standing on what is there and facing the right way. */
export function newPlacement(itemId: string, x: number, y: number, model: MeasuredModel, surfaces: Surfaces): Placement | null {
  const item = catalogueItem(itemId);
  if (!item) return null;
  if (item.mount === 'wall') {
    const w = nearestWall(model, x, y);
    if (w) {
      const inset = item.size[1] / 2 + 0.01;
      return { kind: 'prop', id: newId(), item: itemId, aim: aim(w.x - Math.sin(w.yaw) * inset, w.y + Math.cos(w.yaw) * inset, 0, w.yaw), specs: {} };
    }
  }
  const z = surfaces.at(x, y);
  // on the stage, things face the audience; on the floor, the stage
  const yaw = z > 0.2 ? Math.PI : 0;
  const p: PropPlacement = { kind: 'prop', id: newId(), item: itemId, aim: aim(x, y, z, yaw), specs: {} };
  if (item.person) p.person = { ...item.person };
  return p;
}

/** A block of rows of chairs with the usual sizes, its middle at (x, y), facing the stage. */
export function newRows(itemId: string, x: number, y: number, surfaces: Surfaces): Placement {
  return { kind: 'rows', id: newId(), item: itemId, aim: aim(x, y, surfaces.at(x, y), 0), specs: {}, ...ROW_DEFAULTS };
}

/** How far from a host a print may be put down and still go onto it. Metres. */
const REACH = 2.5;

const hostOf = (p: Placement): string | null => (typeof p.specs.host === 'string' ? p.specs.host : null);

function withoutHost(specs: Placement['specs']): Placement['specs'] {
  const { host: _gone, ...rest } = specs;
  return rest;
}

/**
 * A print that hangs on something (the backdrop print on its frame) goes onto the nearest one within
 * reach, flat on its front; out of reach of any, it stands on its own poles.
 */
export function attach(layout: Layout, p: Placement): Placement {
  const item = catalogueItem(p.item);
  if (p.kind !== 'prop' || !item?.hostedBy) return p;
  let best: { q: Placement; d: number } | null = null;
  for (const q of layout.placements) {
    if (q.item !== item.hostedBy || q.id === p.id) continue;
    const d = Math.hypot(q.aim.x - p.aim.x, q.aim.y - p.aim.y);
    if (d <= REACH && (!best || d < best.d)) best = { q, d };
  }
  if (!best) return hostOf(p) ? { ...p, specs: withoutHost(p.specs) } : p;
  const face = (catalogueItem(best.q.item)?.face ?? 0) + placedSize(p, item)[1] / 2 + 0.002;
  const [x, y] = toHall(best.q.aim, [0, face]);
  return { ...p, aim: { ...best.q.aim, x, y }, specs: { ...p.specs, host: best.q.id } };
}

export function add(layout: Layout, p: Placement): Layout {
  return { ...layout, placements: [...layout.placements, attach(layout, p)] };
}

/**
 * Moves placements by (dx, dy), each settling on what is under its new spot; what hangs on a host goes
 * with it, and a print moved on its own goes onto whatever host is then in reach. Locked ones stay.
 */
export function moveBy(layout: Layout, ids: ReadonlySet<string>, dx: number, dy: number, surfaces: Surfaces, model: MeasuredModel): Layout {
  const withHost = (p: Placement) => {
    const h = hostOf(p);
    return h !== null && ids.has(h);
  };
  const moved = {
    ...layout,
    placements: layout.placements.map((p) => {
      if ((!ids.has(p.id) && !withHost(p)) || p.locked) return p;
      const item = catalogueItem(p.item);
      const x = p.aim.x + dx;
      const y = p.aim.y + dy;
      if (item && onWall(p)) {
        const w = nearestWall(model, x, y);
        if (w) {
          const inset = item.size[1] / 2 + 0.01;
          return { ...p, aim: { ...p.aim, x: w.x - Math.sin(w.yaw) * inset, y: w.y + Math.cos(w.yaw) * inset, yaw: w.yaw } };
        }
      }
      return { ...p, aim: { ...p.aim, x, y, z: surfaces.at(x, y, ids) } };
    }),
  };
  return {
    ...moved,
    placements: moved.placements.map((p) => (ids.has(p.id) && !p.locked && !withHost(p) && catalogueItem(p.item)?.hostedBy ? attach(moved, p) : p)),
  };
}

/** Turns placements by `by` radians, together about their joint middle (one turns about its own); what hangs on them turns too. */
export function rotateBy(layout: Layout, ids: ReadonlySet<string>, by: number): Layout {
  const chosen = layout.placements.filter((p) => ids.has(p.id) && !p.locked && !onWall(p));
  if (!chosen.length) return layout;
  const cx = chosen.reduce((s, p) => s + p.aim.x, 0) / chosen.length;
  const cy = chosen.reduce((s, p) => s + p.aim.y, 0) / chosen.length;
  const c = Math.cos(by);
  const s = Math.sin(by);
  const turned = new Set(chosen.map((p) => p.id));
  for (const p of layout.placements) {
    const h = hostOf(p);
    if (h !== null && turned.has(h) && !p.locked) turned.add(p.id);
  }
  return {
    ...layout,
    placements: layout.placements.map((p) => {
      if (!turned.has(p.id)) return p;
      const dx = p.aim.x - cx;
      const dy = p.aim.y - cy;
      return { ...p, aim: { ...p.aim, x: cx + dx * c - dy * s, y: cy + dx * s + dy * c, yaw: wrap(p.aim.yaw + by) } };
    }),
  };
}

/**
 * Copies of the placements, set off by (dx, dy); returns the layout and the copies' ids. A print copied
 * with its frame hangs on the copy of the frame; one copied alone stands on its own.
 */
export function duplicate(layout: Layout, ids: ReadonlySet<string>, dx: number, dy: number): { layout: Layout; ids: string[] } {
  const fresh = new Map(layout.placements.filter((p) => ids.has(p.id)).map((p) => [p.id, newId()]));
  const copies: Placement[] = [];
  for (const p of layout.placements) {
    if (!ids.has(p.id)) continue;
    const h = hostOf(p);
    const host = h !== null ? fresh.get(h) : undefined;
    const specs = host ? { ...structuredClone(p.specs), host } : withoutHost(structuredClone(p.specs));
    copies.push({ ...structuredClone(p), id: fresh.get(p.id)!, locked: false, specs, aim: { ...p.aim, x: p.aim.x + dx, y: p.aim.y + dy } });
  }
  return { layout: { ...layout, placements: [...layout.placements, ...copies] }, ids: copies.map((c) => c.id) };
}

/** Deletes placements, except locked ones; a print whose frame goes stands on its own. */
export function remove(layout: Layout, ids: ReadonlySet<string>): Layout {
  const gone = new Set(layout.placements.filter((p) => ids.has(p.id) && !p.locked).map((p) => p.id));
  if (!gone.size) return layout;
  return {
    ...layout,
    placements: layout.placements
      .filter((p) => !gone.has(p.id))
      .map((p) => {
        const h = hostOf(p);
        return h !== null && gone.has(h) ? { ...p, specs: withoutHost(p.specs) } : p;
      }),
  };
}

export function setLocked(layout: Layout, ids: ReadonlySet<string>, locked: boolean): Layout {
  return { ...layout, placements: layout.placements.map((p) => (ids.has(p.id) ? { ...p, locked } : p)) };
}

type Edge = 'left' | 'right' | 'back' | 'front' | 'middle-x' | 'middle-y';

/** Lines placements up on one edge (or middle) of the group, as their footprints lie in the hall. */
export function align(layout: Layout, ids: ReadonlySet<string>, edge: Edge): Layout {
  const chosen = layout.placements.filter((p) => ids.has(p.id) && !p.locked);
  if (chosen.length < 2) return layout;
  const box = (p: Placement) => {
    const f = footprint(p);
    return { x0: Math.min(...f.map((c) => c[0])), x1: Math.max(...f.map((c) => c[0])), y0: Math.min(...f.map((c) => c[1])), y1: Math.max(...f.map((c) => c[1])) };
  };
  const boxes = new Map(chosen.map((p) => [p.id, box(p)]));
  const all = [...boxes.values()];
  const target = {
    left: Math.min(...all.map((b) => b.x0)),
    right: Math.max(...all.map((b) => b.x1)),
    back: Math.min(...all.map((b) => b.y0)),
    front: Math.max(...all.map((b) => b.y1)),
    'middle-x': (Math.min(...all.map((b) => b.x0)) + Math.max(...all.map((b) => b.x1))) / 2,
    'middle-y': (Math.min(...all.map((b) => b.y0)) + Math.max(...all.map((b) => b.y1))) / 2,
  }[edge];
  return {
    ...layout,
    placements: layout.placements.map((p) => {
      const b = boxes.get(p.id);
      if (!b) return p;
      let { x, y } = p.aim;
      if (edge === 'left') x += target - b.x0;
      if (edge === 'right') x += target - b.x1;
      if (edge === 'back') y += target - b.y0;
      if (edge === 'front') y += target - b.y1;
      if (edge === 'middle-x') x += target - (b.x0 + b.x1) / 2;
      if (edge === 'middle-y') y += target - (b.y0 + b.y1) / 2;
      return { ...p, aim: { ...p.aim, x, y } };
    }),
  };
}

/** Spreads three or more placements so the gaps between them are equal, the end ones staying put. */
export function distribute(layout: Layout, ids: ReadonlySet<string>, axis: 'x' | 'y'): Layout {
  const chosen = layout.placements.filter((p) => ids.has(p.id) && !p.locked);
  if (chosen.length < 3) return layout;
  const k = axis === 'x' ? 0 : 1;
  const spans = chosen
    .map((p) => {
      const f = footprint(p).map((c) => c[k]);
      return { p, lo: Math.min(...f), hi: Math.max(...f) };
    })
    .sort((a, b) => a.lo - b.lo);
  const total = spans[spans.length - 1]!.hi - spans[0]!.lo;
  const used = spans.reduce((s, x) => s + (x.hi - x.lo), 0);
  const gap = (total - used) / (spans.length - 1);
  const shift = new Map<string, number>();
  let at = spans[0]!.lo;
  for (const s of spans) {
    shift.set(s.p.id, at - s.lo);
    at += s.hi - s.lo + gap;
  }
  return {
    ...layout,
    placements: layout.placements.map((p) => {
      const d = shift.get(p.id);
      if (d === undefined) return p;
      return { ...p, aim: { ...p.aim, x: p.aim.x + (axis === 'x' ? d : 0), y: p.aim.y + (axis === 'y' ? d : 0) } };
    }),
  };
}

/** Breaks a block of rows into single chairs where they stood. */
export function breakRows(layout: Layout, id: string): { layout: Layout; ids: string[] } {
  const rows = layout.placements.find((p) => p.id === id);
  if (!rows || rows.kind !== 'rows') return { layout, ids: [] };
  const item = catalogueItem(rows.item);
  if (!item) return { layout, ids: [] };
  const chairs: Placement[] = rowsGeometry(rows, item.size).seats.map((seat) => {
    const [x, y] = toHall(rows.aim, seat);
    return { kind: 'prop', id: newId(), item: rows.item, aim: { ...rows.aim, x, y }, specs: {} };
  });
  return { layout: { ...layout, placements: [...layout.placements.filter((p) => p.id !== id), ...chairs] }, ids: chairs.map((c) => c.id) };
}

/** Changes a rows block's settings, keeping its front row where it was. */
export function setRows(layout: Layout, id: string, patch: Partial<Pick<Extract<Placement, { kind: 'rows' }>, 'perRow' | 'gap' | 'rows' | 'spacing' | 'aisle'>>): Layout {
  return {
    ...layout,
    placements: layout.placements.map((p) => {
      if (p.id !== id || p.kind !== 'rows') return p;
      const item = catalogueItem(p.item);
      if (!item) return p;
      const next = { ...p, ...patch };
      const before = rowsGeometry(p, item.size).depth;
      const after = rowsGeometry(next, item.size).depth;
      const back = (after - before) / 2;
      const [x, y] = toHall(p.aim, [0, -back]);
      return { ...next, aim: { ...p.aim, x, y } };
    }),
  };
}

/** Where an outline's edges cross the line across the hall at x. */
function crossings(outline: readonly (readonly [number, number])[], x: number): number[] {
  const ys: number[] = [];
  outline.forEach((a, i) => {
    const b = outline[(i + 1) % outline.length]!;
    if (a[0] === b[0] || x < Math.min(a[0], b[0]) || x > Math.max(a[0], b[0])) return;
    ys.push(a[1] + ((x - a[0]) / (b[0] - a[0])) * (b[1] - a[1]));
  });
  return ys;
}

/** Where the nearest wall straight behind (x, y) is, towards the back of the hall. */
function wallBehind(model: MeasuredModel, x: number, y: number): number {
  let best = -Infinity;
  for (const w of model.walls) {
    const [x0, x1] = [Math.min(w.a[0], w.b[0]), Math.max(w.a[0], w.b[0])];
    if (x0 === x1 || x < x0 || x > x1) continue;
    const wy = w.a[1] + ((x - w.a[0]) / (w.b[0] - w.a[0])) * (w.b[1] - w.a[1]);
    if (wy < y && wy > best) best = wy;
  }
  return Number.isFinite(best) ? best : model.bounds.min[1];
}

/**
 * The Mawkeb's four 12 ft carpet strips, side by side on the hall's middle, run back from the stage:
 * to the divider when the hall is split, else their full 74 ft (or to the back wall). Each starts at
 * the stage front where it meets it, so none runs under a curved stage edge.
 */
export function layCarpet(layout: Layout, model: MeasuredModel, toDivider: boolean): { layout: Layout; ids: string[] } {
  const item = catalogueItem('carpet-strip-zahraa');
  const stage = model.solids.find((s) => s.kind === 'stage');
  const front = model.references.find((r) => r.id === 'back_to_stage_front_centre');
  if (!item || !stage || !front) return { layout, ids: [] };
  const [w, full, h] = item.size;
  const cx = front.b[0];
  const strips: Placement[] = [];
  for (let k = 0; k < 4; k++) {
    const x0 = cx + (k - 2) * w;
    // the stage edge within the strip: its corners there, and where it crosses the strip's sides
    const near = [
      ...stage.outline.filter((p) => p[0] >= x0 && p[0] <= x0 + w).map((p) => p[1]),
      ...crossings(stage.outline, x0),
      ...crossings(stage.outline, x0 + w),
    ];
    const start = near.length ? Math.min(...near) : front.b[1];
    const back = Math.max(...[0, 0.25, 0.5, 0.75, 1].map((f) => wallBehind(model, x0 + f * w, start))) + 0.02;
    const end = Math.max(back, toDivider ? front.b[1] - layout.divider.fromStage : start - full);
    const length = Math.min(full, start - end);
    if (length <= 0.5) continue;
    strips.push({ kind: 'prop', id: newId(), item: item.id, aim: aim(x0 + w / 2, start - length / 2, 0, 0), size: [w, length, h], specs: {} });
  }
  return { layout: { ...layout, placements: [...strips, ...layout.placements] }, ids: strips.map((s) => s.id) };
}

/** Heights people are drawn at: a few per dress, so a crowd varies but still draws as a handful of batches. */
const HEIGHTS = { men: [1.66, 1.73, 1.81], women: [1.55, 1.61, 1.68] } as const;
const BUILDS = ['slim', 'medium', 'medium', 'broad'] as const;

/** A repeatable sequence of pseudo-random numbers, so the same fill always looks the same. */
function seeded(seed: number) {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) % 100000) / 100000;
  };
}

/** Room each person takes sitting cross-legged on a carpet: across, and front to back. */
const FLOOR_PITCH = [0.72, 0.86] as const;

interface Spot {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pose: 'floor' | 'chair';
}

/** Where people can still sit on the chosen carpet strips and rows (a place someone already has is left out), front first. */
function seatSpots(layout: Layout, ids: ReadonlySet<string>): Spot[] {
  const spots: Spot[] = [];
  for (const p of layout.placements) {
    if (!ids.has(p.id)) continue;
    const item = catalogueItem(p.item);
    if (!item) continue;
    if (p.kind === 'rows') {
      for (const seat of rowsGeometry(p, item.size).seats) {
        const [x, y] = toHall(p.aim, seat);
        spots.push({ x, y, z: p.aim.z, yaw: p.aim.yaw, pose: 'chair' });
      }
    } else if (item.make === 'carpet') {
      const [w, d, h] = placedSize(p, item);
      const cols = Math.floor((w - 0.2) / FLOOR_PITCH[0]);
      const rows = Math.floor((d - 0.2) / FLOOR_PITCH[1]);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const lx = -((cols - 1) * FLOOR_PITCH[0]) / 2 + c * FLOOR_PITCH[0] + (r % 2 ? FLOOR_PITCH[0] / 4 : -FLOOR_PITCH[0] / 4);
          const ly = d / 2 - 0.45 - r * FLOOR_PITCH[1];
          const [x, y] = toHall(p.aim, [lx, ly]);
          spots.push({ x, y, z: p.aim.z + h, yaw: p.aim.yaw, pose: 'floor' });
        }
      }
    }
  }
  const seated = layout.placements.filter((p) => p.kind === 'prop' && p.person).map((p) => [p.aim.x, p.aim.y] as const);
  const free = spots.filter((s) => !seated.some(([x, y]) => Math.hypot(x - s.x, y - s.y) < 0.3));
  // nearest the stage first
  return free.sort((a, b) => b.y - a.y);
}

/** How many more people can sit on the chosen carpet strips and rows. */
export function freeSeats(layout: Layout, ids: ReadonlySet<string>): number {
  return seatSpots(layout, ids).length;
}

/**
 * Fills carpet strips or rows of chairs with seated people, front first: cross-legged on carpet, on
 * the chairs in rows. Each sits a little off the grid and turned a little, as people do.
 */
export function fillSeated(layout: Layout, ids: ReadonlySet<string>, count: number, dress: 'men' | 'women'): { layout: Layout; ids: string[] } {
  const spots = seatSpots(layout, ids);
  const rnd = seeded(Math.round(count * 7919 + spots.length * 104729));
  const people: Placement[] = spots.slice(0, Math.max(0, count)).map((s) => {
    const jitter = s.pose === 'floor' ? 0.07 : 0;
    const height = HEIGHTS[dress][Math.floor(rnd() * 3)]!;
    const build = BUILDS[Math.floor(rnd() * BUILDS.length)]!;
    const itemId = s.pose === 'floor' ? (dress === 'men' ? 'person-man-floor' : 'person-woman-floor') : dress === 'men' ? 'person-man-standing' : 'person-woman-standing';
    return {
      kind: 'prop', id: newId(), item: itemId, specs: {},
      aim: aim(s.x + (rnd() - 0.5) * 2 * jitter, s.y + (rnd() - 0.5) * 2 * jitter, s.z, wrap(s.yaw + (rnd() - 0.5) * (s.pose === 'floor' ? 0.35 : 0.12))),
      person: { dress, pose: s.pose, height, build },
    };
  });
  return { layout: { ...layout, placements: [...layout.placements, ...people] }, ids: people.map((p) => p.id) };
}

export function itemOf(p: Placement): CatalogueItem | undefined {
  return catalogueItem(p.item);
}

export function sizeOf(p: Placement): [number, number, number] | null {
  const item = catalogueItem(p.item);
  if (!item) return null;
  if (p.kind === 'rows') {
    const g = rowsGeometry(p, item.size);
    return [g.width, g.depth, item.size[2]];
  }
  return placedSize(p, item);
}
